import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import OTPInputSingle from './OTPInputSingle.vue';
@Component({
    name: 'OTPInput',
    components: {
        OTPInputSingle,
    },
    // Only inherit field status attributes.
    // @see get fieldStatusAttrs()
    // @see https://v2.vuejs.org/v2/guide/components-props#Disabling-Attribute-Inheritance
    // @see https://v2.vuejs.org/v2/api/#inheritAttrs
    inheritAttrs: false,
})
export default class default_1 extends Vue {
    @Prop({ default: 6 })
    numInputs;
    @Prop({ default: 'text' })
    inputType;
    @Prop({ default: 'text' })
    inputMode;
    @Prop({ default: ' '.repeat(6) })
    value;
    @Prop({ default: false })
    autofocus;
    @Prop({ default: false })
    autocapitalize;
    @Prop({ default: false })
    blurOnComplete;
    activeInputIndex = this.autofocus ? 0 : -1;
    emptyChar = ' ';
    emptyString = this.emptyChar.repeat(this.numInputs);
    localValue = this.emptyString;
    @Emit('on-change')
    emitOnChange(fullValue) {
        return fullValue;
    }
    @Emit('on-focus')
    handleFocus(event, index) {
        this.activeInputIndex = index;
        return event;
    }
    @Emit('on-blur')
    emitOnBlur() { }
    @Emit('on-complete')
    handleComplete(fullValue) {
        return fullValue;
    }
    @Watch('value', { immediate: true })
    onValueChange(newValue) {
        this.localValue = newValue.padEnd(this.numInputs, this.emptyChar);
    }
    get canChange() {
        return !this.$attrs.readonly && !this.$attrs.disabled;
    }
    get fieldsetId() {
        return `${this.$attrs.id}-fieldset`;
    }
    get fieldStatusAttrs() {
        // we only want to pass a subset of the attrs to the single input component
        const { readonly, disabled, required, invalid, 'aria-invalid': ariaInvalid } = this.$attrs;
        return { readonly, disabled, required, invalid, 'aria-invalid': ariaInvalid };
    }
    get items() {
        return Array.from({ length: this.numInputs }, (_, i) => ({
            id: `${this.$attrs.id}-${i}`,
            value: this.localValue[i],
            label: `Character ${i + 1} of ${this.numInputs}`,
            hasFocus: this.activeInputIndex === i,
            autoFocus: this.autofocus && i === 0,
        }));
    }
    handleKeyDown(event) {
        const key = event.key;
        const code = event.code;
        if (key === 'Backspace' || code === 'Backspace') {
            event.preventDefault();
            this.changeCharAtFocus(this.emptyChar, 'prev');
            return;
        }
        if (key === 'Delete' || code === 'Delete') {
            event.preventDefault();
            this.changeCharAtFocus(this.emptyChar);
            return;
        }
        if (key === 'ArrowLeft' || code === 'ArrowLeft') {
            event.preventDefault();
            this.focusPrevInput();
            return;
        }
        if (key === 'ArrowRight' || code === 'ArrowRight') {
            event.preventDefault();
            this.focusNextInput();
            return;
        }
        if (key === 'Enter' || code === 'Enter' || code === 'NumpadEnter') {
            event.preventDefault();
            this.handleComplete(this.localValue);
            this.forceBlur();
        }
    }
    handleChange(charValue) {
        if (this.canChange)
            this.changeCharAtFocus(charValue, 'next');
    }
    changeCharAtFocus(char, focusTo) {
        if (!this.canChange)
            return;
        if (char !== this.localValue[this.activeInputIndex]) {
            const start = this.localValue.slice(0, this.activeInputIndex);
            const end = this.localValue.slice(this.activeInputIndex + 1);
            this.localValue = `${start}${char}${end}`;
            this.emitOnChange(this.localValue);
        }
        if (focusTo === 'next') {
            if (this.activeInputIndex === this.numInputs - 1)
                this.checkFilledAllInputs();
            else if (this.activeInputIndex !== -1)
                this.focusNextInput();
        }
        else if (focusTo === 'prev') {
            if (this.activeInputIndex > 0)
                this.focusPrevInput();
        }
    }
    focusInput(input) {
        const maxInput = Math.min(this.numInputs - 1, input);
        this.activeInputIndex = Math.max(maxInput, 0);
    }
    focusNextInput() {
        this.focusInput(this.activeInputIndex + 1);
    }
    focusPrevInput() {
        this.focusInput(this.activeInputIndex - 1);
    }
    checkFilledAllInputs() {
        if (this.localValue.trim().length < this.numInputs)
            return;
        // nextTick to prevent race condition with the change event
        this.$nextTick(() => {
            this.handleComplete(this.localValue);
            if (this.blurOnComplete)
                this.forceBlur();
        });
    }
    handlePaste(event) {
        if (!this.canChange)
            return;
        event.preventDefault();
        const clipboardData = event.clipboardData;
        if (!clipboardData)
            return;
        const rawClipboard = clipboardData.getData('text/plain');
        if (this.inputType === 'number' && /[^0-9]/.test(rawClipboard))
            return;
        const invalidPattern = /[^a-zA-Z0-9]/g;
        const pastedData = rawClipboard.replace(invalidPattern, '').slice(0, this.numInputs - this.activeInputIndex);
        if (!pastedData)
            return;
        // Paste data from focused input onwards
        const newValue = this.localValue.slice(0, this.activeInputIndex).concat(pastedData).slice(0, this.numInputs);
        this.localValue = this.autocapitalize ? newValue.toUpperCase() : newValue;
        this.focusInput(newValue.length);
        this.emitOnChange(newValue);
        this.checkFilledAllInputs();
    }
    handleBlur(input) {
        // if the input is still marked as active in the next tick, it means no other input has been focused (via focusNext/focusPrev), therefore the input component itself has lost focus (e.g. user clicked outside the input)
        this.$nextTick(() => {
            if (this.activeInputIndex === input) {
                this.activeInputIndex = -1;
                this.emitOnBlur();
            }
        });
    }
    forceBlur() {
        const activeInputId = this.items[this.activeInputIndex]?.id;
        const activeInputRef = this.$refs[activeInputId];
        // safely check the active index actually corresponds to a valid element
        if (Array.isArray(activeInputRef) && activeInputRef.length > 0) {
            const el = activeInputRef[0].$el;
            el.blur();
        }
        this.emitOnBlur();
        this.activeInputIndex = -1;
    }
}
